import React, { useState, useEffect } from "react";
import Breadcrumb from "../../../components/Breadcrumb";
import Grid from "../../../components/Grid";
import VouchersService from "../../../Api/VouchersService";
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import Swal from 'sweetalert2/dist/sweetalert2.js';

const columns = [
    { 
      header: 'Code',
      key: 'code',
    },
    {
      header: 'Voucher Type',
      key: 'voucher_type',
    },
    {
      header: 'Discount Value',
      key: 'discount_value',
      render: (item) => (
        <span>{item.voucher_type === 'percentage' ? `${item.discount_value}%` : `₹${item.discount_value}`}</span>
      )
    },
    {
      header: 'Product Type',
      key: 'product_type',
    },
    {
      header: 'Usage Limit',
      key: 'usage_limit',
    },
    {
      header: 'Times Used',
      key: 'times_used',
    },
    {
      header: 'Status',
      key: 'is_fully_redeemed',
      render: (item) => (
        <span className={`badge ${item.is_fully_redeemed ? 'bg-danger' : 'bg-success'}`}>
          {item.is_fully_redeemed ? 'Fully Redeemed' : 'Available'}
        </span>
      )
    },
    {
      header: 'Valid Until',
      key: 'valid_until',
      render: (item) => (
        <span>{new Date(item.valid_until).toLocaleDateString()}</span>
      )
    }
];

const VouchersList = () => {
  const navigate = useNavigate();
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const voucherAPI = new VouchersService();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const fetchVouchers = async () => {
    try {
      setLoading(true);
      const response = await voucherAPI.GetAllVouchers();
      if (response.data && response.data.data) {
        setVouchers(response.data.data);
        toast.success('Vouchers loaded successfully!');
      }
    } catch (err) {
      console.error("Error fetching vouchers:", err);
      toast.error("Failed to load vouchers. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVouchers();
  }, []);

  const handleItemsPerPage = (perPage) => {
    setItemsPerPage(perPage);
    setCurrentPage(1);
  };

  const handleEdit = (voucher) => {
    navigate(`/add-voucher?id=${voucher.id}`);
  };

  const handleView = (voucher) => {
    navigate(`/view-voucher?id=${voucher.id}`);
  };

  const handleDelete = async (voucher) => {
    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!',
        showLoaderOnConfirm: true,
        preConfirm: async () => {
          try {
            const response = await voucherAPI.DeleteById(voucher.id);
            if (response.data && response.data.status === "success") {
              return true;
            } else {
              throw new Error(response.data?.message || 'Failed to delete voucher');
            }
          } catch (error) {
            Swal.showValidationMessage(
              `Delete failed: ${error.response?.data?.message || error.message}`
            );
          }
        },
        allowOutsideClick: () => !Swal.isLoading()
      });

      if (result.isConfirmed) {
        await Swal.fire({
          title: 'Deleted!',
          text: 'Voucher has been deleted.',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false
        });
        toast.success('Voucher deleted successfully!');
        fetchVouchers();
      }
    } catch (error) {
      console.error("Error in delete operation:", error);
      toast.error('An unexpected error occurred');
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <Breadcrumb title="Vouchers List" />
      
      <Grid 
        data={vouchers}
        columns={columns}
        add="add-voucher"
        onEdit={handleEdit}
        onDelete={handleDelete}
        onView={handleView}
        onItemsPerPageChange={handleItemsPerPage}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        itemsPerPage={itemsPerPage}
        showSearch={false}
        showStatusFilter={false}
      />
    </>
  );
};

export default VouchersList; 
